import { BookingFragment, BookingSeriesFragment } from "src/state/gql/_types"
import { FormSchema } from "src/utils/forms"

export type SuperAdminBookingCreateFragment =
  Omit<BookingFragment, 'id' | 'status' | 'created_at' | 'updated_at' | 'created_by_user' | 'updated_by_user' | 'booking_status' | 'series_id' | 'is_master' | 'booking_rate_type' | 'booking_invoice_target' | 'booking_type' | 'guidelines'> &
  Omit<BookingSeriesFragment, 'id' | 'master_booking_id' | 'created_at' | 'updated_at' | 'created_by' | 'updated_by' | 'next_expansion' | 'start_date'>

export const SuperAdminBookingCreateSchema: FormSchema<SuperAdminBookingCreateFragment> = {
    id: "booking",
    title: "Booking",
    groups: {
        default: {
            label: 'Default' 
        },
        recurring: {
            label: 'Repeat' 
        }
    },
    fields: {
        type: {
            group: 'default',
            label: "Booking Type",
            type: "booking-type-enum",
            grid: 12,
            validation: {
                required: {
                    value: false,
                    message: '{field} is required'
                }
            }
        },
        venue_id: {
            group: 'default',
            label: "Venue",
            type: "superadmin-booking-venue-select",
            grid: 6,
            validation: {
                required: {
                    value: true,
                    message: '{field} is required'
                }
            }
        },
        area_id: {
            group: 'default',
            label: "Area",
            type: "superadmin-venue-area-select",
            grid: 6,
            validation: {
                required: {
                    value: false,
                    message: '{field} is required'
                }
            }
        },
        talent_id: {
            group: 'default',
            label: "Talent",
            type: "superadmin-booking-talent-select",
            grid: 12,
            validation: {
                required: {
                    value: false,
                    message: '{field} is required'
                }
            }
        },
        date: {
            group: 'default',
            label: "Date",
            type: "date-picker",
            grid: 12,
            validation: {
                required: {
                    value: true,
                    message: '{field} is required'
                }
            }
        },
        event_start_time: {
            group: 'default',
            label: "Event Start Time",
            type: "time-picker",
            grid: 6,
            validation: {
                required: {
                    value: false,
                    message: '{field} is required'
                }
            }
        },
        event_end_time: {
            group: 'default',
            label: "Event End Time",
            type: "time-picker",
            grid: 6,
            validation: {
                required: {
                    value: false,
                    message: '{field} is required'
                }
            }
        },
        set_start_time: {
            group: 'default',
            label: "Set Start Time",
            type: "time-picker",
            grid: 6,
            validation: {
                required: {
                    value: true,
                    message: '{field} is required'
                }
            }
        },
        set_end_time: {
            group: 'default',
            label: "Set End Time",
            type: "time-picker",
            grid: 6,
            validation: {
                required: {
                    value: true,
                    message: '{field} is required'
                }
            }
        },
        rate: {
            group: 'default',
            label: "Rate",
            type: "currency",
            grid: 4,
            validation: {
                required: {
                    value: false,
                    message: '{field} is required'
                }
            }
        },
        rate_type: {
            group: 'default',
            label: "Rate Type",
            type: "booking-rate-type-enum",
            grid: 4,
            validation: {
                required: {
                    value: false,
                    message: '{field} is required'
                }
            }
        },
        rate_margin: {
            group: 'default',
            label: "Rate Margin",
            type: "text",
            grid: 4,
            validation: {
                required: {
                    value: false,
                    message: '{field} is required'
                }
            }
        },
        invoice_target: {
            group: 'default',
            label: "Invoice Target",
            type: "booking-invoice-target-enum",
            grid: 4,
            validation: {
                required: {
                    value: false,
                    message: '{field} is required'
                }
            }
        },
        poc: {
            group: 'default',
            label: "Point of Contact",
            type: "text",
            grid: 4,
            validation: {
                required: {
                    value: false,
                    message: '{field} is required'
                }
            }
        },
        notes: {
            group: 'default',
            label: "Notes",
            type: "textarea",
            grid: 12,
            validation: {
                required: {
                    value: false,
                    message: '{field} is required'
                }
            }
        },
        recurring: {
            group: 'recurring',
            label: "Repeat Booking",
            type: "recurring-booking-config",
            grid: 12,
            validation: {
                required: {
                    value: false,
                    message: '{field} is required'
                }
            }
        },
        end_date: {
            group: 'recurring',
            label: "End Date",
            type: "date-picker",
            grid: 12,
            validation: {
                required: {
                    value: false,
                    message: '{field} is required'
                }
            },
            skipRender: true
        },
        frequency: {
            group: 'recurring',
            label: "Frequency",
            type: "text",
            grid: 12,
            validation: {
                required: {
                    value: false,
                    message: '{field} is required'
                }
            },
            skipRender: true
        },
        interval: {
            group: 'recurring',
            label: "Interval",
            type: "text",
            grid: 12,
            validation: {
                required: {
                    value: false,
                    message: '{field} is required'
                }
            },
            skipRender: true
        },
        count: {
            group: 'recurring',
            label: "Count",
            type: "text",
            grid: 12,
            validation: {
                required: {
                    value: false,
                    message: '{field} is required'
                }
            },
            skipRender: true
        },
        by_day: {
            group: 'recurring',
            label: "By Day",
            type: "text",
            grid: 12,
            validation: {
                required: {
                    value: false,
                    message: '{field} is required'
                }
            },
            skipRender: true
        },
        by_month_day: {
            group: 'recurring',
            label: "By Month Day",
            type: "text",
            grid: 12,
            validation: {
                required: {
                    value: false,
                    message: '{field} is required'
                }
            },
            skipRender: true
        },
        rrule: {
            group: 'recurring',
            label: "Rrule",
            type: "textarea",
            grid: 12,
            validation: {
                required: {
                    value: false,
                    message: '{field} is required'
                }
            },
            skipRender: true
        },
        timezone: {
            group: 'recurring',
            label: "Timezone",
            type: "text",
            grid: 12,
            validation: {
                required: {
                    value: false,
                    message: '{field} is required'
                }
            },
            skipRender: true
        },
        
    }
}

export type SuperAdminBookingUpdateFragment = Omit<BookingFragment, 'id' | 'created_at' | 'updated_at' | 'created_by_user' | 'updated_by_user' | 'booking_status' | 'series_id' | 'is_master' | 'recurring' | 'booking_rate_type' | 'booking_invoice_target' | 'booking_type' | 'guidelines'>

export const SuperAdminBookingUpdateSchema: FormSchema<SuperAdminBookingUpdateFragment> = {
    id: "booking",
    title: "Booking",
    groups: {
        default: {
            label: 'Default' 
        }
    },
    fields: {
        status: {
            group: 'default',
            label: "Status",
            type: "superadmin-booking-status-enum",
            grid: 12,
            validation: {
                required: {
                    value: true,
                    message: '{field} is required'
                }
            }
        },
        type: {
            group: 'default',
            label: "Booking Type",
            type: "booking-type-enum",
            grid: 12,
            validation: {
                required: {
                    value: false,
                    message: '{field} is required'
                }
            }
        },
        venue_id: {
            group: 'default',
            label: "Venue",
            type: "superadmin-booking-venue-select",
            grid: 6,
            validation: {
                required: {
                    value: true,
                    message: '{field} is required'
                }
            }
        },
        area_id: {
            group: 'default',
            label: "Area",
            type: "superadmin-venue-area-select",
            grid: 6,
            validation: {
                required: {
                    value: false,
                    message: '{field} is required'
                }
            }
        },
        talent_id: {
            group: 'default',
            label: "Talent",
            type: "superadmin-booking-talent-select",
            grid: 12,
            validation: {
                required: {
                    value: false,
                    message: '{field} is required'
                }
            }
        },
        date: {
            group: 'default',
            label: "Date",
            type: "date-picker",
            grid: 12,
            validation: {
                required: {
                    value: true,
                    message: '{field} is required'
                }
            }
        },
        event_start_time: {
            group: 'default',
            label: "Event Start Time",
            type: "time-picker",
            grid: 6,
            validation: {
                required: {
                    value: false,
                    message: '{field} is required'
                }
            }
        },
        event_end_time: {
            group: 'default',
            label: "Event End Time",
            type: "time-picker",
            grid: 6,
            validation: {
                required: {
                    value: false,
                    message: '{field} is required'
                }
            }
        },
        set_start_time: {
            group: 'default',
            label: "Set Start Time",
            type: "time-picker",
            grid: 6,
            validation: {
                required: {
                    value: true,
                    message: '{field} is required'
                }
            }
        },
        set_end_time: {
            group: 'default',
            label: "Set End Time",
            type: "time-picker",
            grid: 6,
            validation: {
                required: {
                    value: true,
                    message: '{field} is required'
                }
            }
        },
        rate: {
            group: 'default',
            label: "Rate",
            type: "currency",
            grid: 4,
            validation: {
                required: {
                    value: false,
                    message: '{field} is required'
                }
            }
        },
        rate_type: {
            group: 'default',
            label: "Rate Type",
            type: "booking-rate-type-enum",
            grid: 4,
            validation: {
                required: {
                    value: false,
                    message: '{field} is required'
                }
            }
        },
        rate_margin: {
            group: 'default',
            label: "Rate Margin",
            type: "text",
            grid: 4,
            validation: {
                required: {
                    value: false,
                    message: '{field} is required'
                }
            }
        },
        invoice_target: {
            group: 'default',
            label: "Invoice Target",
            type: "booking-invoice-target-enum",
            grid: 4,
            validation: {
                required: {
                    value: false,
                    message: '{field} is required'
                }
            }
        },
        poc: {
            group: 'default',
            label: "Point of Contact",
            type: "text",
            grid: 4,
            validation: {
                required: {
                    value: false,
                    message: '{field} is required'
                }
            }
        },
        notes: {
            group: 'default',
            label: "Notes",
            type: "textarea",
            grid: 12,
            validation: {
                required: {
                    value: false,
                    message: '{field} is required'
                }
            }
        }
    }
}